
import * as util from 'util'

import * as big from '../big'
import * as int64 from '../int64'
import * as uuid from '../uuid'

import * as fbe from '../fbe'
import * as core from '../core'
import * as config from '../config'
import * as history from '../history'
import * as client from '../client'
import * as feed from '../feed'
import * as storage from '../storage'
import * as exposed from '../exposed'
import * as execution from '../execution'
import * as backup from '../backup'
import * as inner from '../inner'
import * as trade from '../trade'
import * as security from '../security'
import * as document from '../document'

const Big = big.Big // eslint-disable-line
const Int64 = int64.Int64 // eslint-disable-line
const UInt64 = int64.UInt64 // eslint-disable-line
const UUID = uuid.UUID // eslint-disable-line
import {ConfigRequest} from './ConfigRequest';
import {FieldModelConfigRequest} from './ConfigRequest';
import {ConfigHistoryResponse} from './ConfigHistoryResponse';

/**
 * ConfigHistoryRequest struct
 */
class ConfigHistoryRequest extends ConfigRequest {
  /**
   * Initialize struct
   * @param {!ConfigRequest=} parent
   * @param {!number=} count
   * @constructor
   */
  constructor (parent = new ConfigRequest(), count = 100) {
    super()
    super.copy(parent)
    this.Count = count
  }

  /**
   * Copy struct (shallow copy)
   * @this {!ConfigHistoryRequest}
   * @param {!ConfigHistoryRequest} other Other struct
   * @returns {!ConfigHistoryRequest} This struct
   */
  copy (other) {
    super.copy(other)
    if (other.Count != null) {
      this.Count = other.Count
    } else {
      this.Count = null
    }
    return this
  }

  /**
   * Clone struct (deep clone)
   * @this {!ConfigHistoryRequest}
   * @returns {!ConfigHistoryRequest} Cloned struct
   */
  clone () {
    // Serialize the struct to the FBE stream
    let writer = new ConfigHistoryRequestModel(new fbe.WriteBuffer())
    writer.serialize(this)

    // Deserialize the struct from the FBE stream
    let reader = new ConfigHistoryRequestModel(new fbe.ReadBuffer())
    reader.attachBuffer(writer.buffer)
    return reader.deserialize().value
  }

  /**
   * Is this struct equal to other one?
   * @this {!ConfigHistoryRequest}
   * @param {!ConfigHistoryRequest} other Other struct
   * @returns {boolean} Equal result
   */
  eq (other) {
    if (!(other instanceof ConfigHistoryRequest)) {
      throw new TypeError('Instance of ConfigHistoryRequest is required!')
    }
    // noinspection RedundantIfStatementJS
    if (!super.eq(other)) {
      return false
    }
    return true
  }

  /**
   * Convert struct to JSON
   * @this {!ConfigHistoryRequest}
   * @returns {!object} Struct value for JSON
   */
  toJSON () {
    let parent = super.toJSON()
    let current = {
      Count: ((this.Count != null) ? this.Count : null)
    }
    return { ...parent, ...current }
  }

  /**
   * Convert JSON to struct
   * @param {!string} json JSON string
   * @returns {!object} Struct value for JSON
   */
  static fromJSON (json) {
    return ConfigHistoryRequest.fromObject(JSON.parse(json))
  }

  /**
   * Create struct from object value
   * @param {!ConfigHistoryRequest} other Object value
   * @returns {!ConfigHistoryRequest} Created struct
   */
  static fromObject (other) {
    return new ConfigHistoryRequest().copy(other)
  }

  /**
   * Get the FBE type
   * @this {!ConfigHistoryRequest}
   * @returns {!number} FBE type
   */
  get fbeType () {
    return ConfigHistoryRequest.fbeType
  }

  /**
   * Get the FBE type (static)
   * @this {!ConfigHistoryRequest}
   * @returns {!number} FBE type
   */
  static get fbeType () {
    return 6031
  }
}

export { ConfigHistoryRequest };

/**
 * Fast Binary Encoding ConfigHistoryRequest field model
 */
class FieldModelConfigHistoryRequest extends fbe.FieldModel {
  /**
   * Initialize field model with the given buffer and offset
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Buffer
   * @param {!number} offset Offset
   * @constructor
   */
  constructor (buffer, offset) {
    super(buffer, offset)
    this._parent = new FieldModelConfigRequest(buffer, 4 + 4)
    this.Count = new fbe.FieldModelUInt32(buffer, this._parent.fbeOffset + this._parent.fbeBody - 4 - 4)
  }

  /**
   * Get the ConfigRequest field model
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!FieldModelConfigRequest} ConfigRequest field model
   */
  get parent () {
    return this._parent
  }

  /**
   * Get the field size
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!number} Field size
   */
  get fbeSize () {
    return 4
  }

  /**
   * Get the field body size
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!number} Field body size
   */
  get fbeBody () {
    return 4 + 4 + this.parent.fbeBody - 4 - 4 + this.Count.fbeSize
  }

  /**
   * Get the field extra size
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!number} Field extra size
   */
  get fbeExtra () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4) > this._buffer.size)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)

    let fbeResult = this.fbeBody + this.parent.fbeExtra + this.Count.fbeExtra

    this._buffer.unshift(fbeStructOffset)

    return fbeResult
  }

  /**
   * Get the field type
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!number} Field type
   */
  get fbeType () {
    return FieldModelConfigHistoryRequest.fbeType
  }

  /**
   * Get the field type (static)
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!number} Field type
   */
  static get fbeType () {
    return 6031
  }

  /**
   * Check if the struct value is valid
   * @this {!FieldModelConfigHistoryRequest}
   * @param {!boolean} fbeVerifyType Verify model type flag, defaults is true
   * @returns {!boolean} Field model valid state
   */
  verify (fbeVerifyType = true) {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return true
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return false
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    if (fbeStructSize < (4 + 4)) {
      return false
    }

    let fbeStructType = this.readUInt32(fbeStructOffset + 4)
    if (fbeVerifyType && (fbeStructType !== this.fbeType)) {
      return false
    }

    this._buffer.shift(fbeStructOffset)
    let fbeResult = this.verifyFields(fbeStructSize)
    this._buffer.unshift(fbeStructOffset)
    return fbeResult
  }

  /**
   * Check if the struct fields are valid
   * @this {!FieldModelConfigHistoryRequest}
   * @param {!number} fbeStructSize FBE struct size
   * @returns {!boolean} Field model valid state
   */
  verifyFields (fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) > fbeStructSize) {
      return true
    }
    if (!this.parent.verifyFields(fbeStructSize)) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Count.fbeSize) > fbeStructSize) {
      return true
    }
    if (!this.Count.verify()) {
      return false
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Count.fbeSize

    return true
  }

  /**
   * Get the struct value (begin phase)
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!number} Field model begin offset
   */
  getBegin () {
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructOffset = this.readUInt32(this.fbeOffset)
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + 4 + 4) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset === 0) || ((this._buffer.offset + fbeStructOffset + 4 + 4) > this._buffer.size)) {
      return 0
    }

    let fbeStructSize = this.readUInt32(fbeStructOffset)
    console.assert((fbeStructSize >= (4 + 4)), 'Model is broken!')
    if (fbeStructSize < (4 + 4)) {
      return 0
    }

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Get the struct value (end phase)
   * @this {!FieldModelConfigHistoryRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  getEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Get the struct value
   * @this {!FieldModelConfigHistoryRequest}
   * @param {!ConfigHistoryRequest} fbeValue Default value, defaults is new ConfigHistoryRequest()
   * @returns {!ConfigHistoryRequest} ConfigHistoryRequest value
   */
  get (fbeValue = new ConfigHistoryRequest()) {
    let fbeBegin = this.getBegin()
    if (fbeBegin === 0) {
      return fbeValue
    }

    let fbeStructSize = this.readUInt32(0)
    this.getFields(fbeValue, fbeStructSize)
    this.getEnd(fbeBegin)
    return fbeValue
  }

  /**
   * Get the struct fields values
   * @this {!FieldModelConfigHistoryRequest}
   * @param {!ConfigHistoryRequest} fbeValue ConfigHistoryRequest value
   * @param {!number} fbeStructSize Struct size
   */
  getFields (fbeValue, fbeStructSize) {
    let fbeCurrentSize = 4 + 4

    if ((fbeCurrentSize + this.parent.fbeBody - 4 - 4) <= fbeStructSize) {
      this.parent.getFields(fbeValue, fbeStructSize)
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.parent.fbeBody - 4 - 4

    if ((fbeCurrentSize + this.Count.fbeSize) <= fbeStructSize) {
      fbeValue.Count = this.Count.get(100)
    } else {
      fbeValue.Count = 100
    }
    // noinspection JSUnusedAssignment
    fbeCurrentSize += this.Count.fbeSize
  }

  /**
   * Set the struct value (begin phase)
   * @this {!FieldModelConfigHistoryRequest}
   * @returns {!number} Field model begin offset
   */
  setBegin () {
    console.assert(((this._buffer.offset + this.fbeOffset + this.fbeSize) <= this._buffer.size), 'Model is broken!')
    if ((this._buffer.offset + this.fbeOffset + this.fbeSize) > this._buffer.size) {
      return 0
    }

    let fbeStructSize = this.fbeBody
    let fbeStructOffset = this._buffer.allocate(fbeStructSize) - this._buffer.offset
    console.assert((fbeStructOffset > 0) && ((this._buffer.offset + fbeStructOffset + fbeStructSize) <= this._buffer.size), 'Model is broken!')
    if ((fbeStructOffset <= 0) || ((this._buffer.offset + fbeStructOffset + fbeStructSize) > this._buffer.size)) {
      return 0
    }

    this.writeUInt32(this.fbeOffset, fbeStructOffset)
    this.writeUInt32(fbeStructOffset, fbeStructSize)
    this.writeUInt32(fbeStructOffset + 4, this.fbeType)

    this._buffer.shift(fbeStructOffset)
    return fbeStructOffset
  }

  /**
   * Set the struct value (end phase)
   * @this {!FieldModelConfigHistoryRequest}
   * @param {!number} fbeBegin Field model begin offset
   */
  setEnd (fbeBegin) {
    this._buffer.unshift(fbeBegin)
  }

  /**
   * Set the struct value
   * @this {!FieldModelConfigHistoryRequest}
   * @param {!ConfigHistoryRequest} fbeValue ConfigHistoryRequest value
   */
  set (fbeValue) {
    let fbeBegin = this.setBegin()
    if (fbeBegin === 0) {
      return
    }

    this.setFields(fbeValue)
    this.setEnd(fbeBegin)
  }

  /**
   * Set the struct fields values
   * @this {!FieldModelConfigHistoryRequest}
   * @param {ConfigHistoryRequest} fbeValue ConfigHistoryRequest value
   */
  setFields (fbeValue) {
    this.parent.setFields(fbeValue)
    this.Count.set(fbeValue.Count)
  }
}

export { FieldModelConfigHistoryRequest };

/**
 * Fast Binary Encoding ConfigHistoryRequest model
 */
class ConfigHistoryRequestModel extends fbe.Model {
  /**
   * Initialize model with the given buffer
   * @param {!fbe.ReadBuffer|!fbe.WriteBuffer} buffer Read/Write buffer, defaults is new fbe.WriteBuffer()
   * @constructor
   */
  constructor (buffer = new fbe.WriteBuffer()) {
    super(buffer)
    this._model = new FieldModelConfigHistoryRequest(this.buffer, 4)
  }

  /**
   * Get the ConfigHistoryRequest model
   * @this {!ConfigHistoryRequestModel}
   * @returns {!FieldModelConfigHistoryRequest} model ConfigHistoryRequest model
   */
  get model () {
    return this._model
  }

  /**
   * Get the model size
   * @this {!ConfigHistoryRequestModel}
   * @returns {!number} Model size
   */
  get fbeSize () {
    return this.model.fbeSize + this.model.fbeExtra
  }

  /**
   * Get the model type
   * @this {!ConfigHistoryRequestModel}
   * @returns {!number} Model type
   */
  get fbeType () {
    return ConfigHistoryRequestModel.fbeType
  }

  /**
   * Get the model type (static)
   * @this {!ConfigHistoryRequestModel}
   * @returns {!number} Model type
   */
  static get fbeType () {
    return FieldModelConfigHistoryRequest.fbeType
  }

  /**
   * Check if the struct value is valid
   * @this {!ConfigHistoryRequestModel}
   * @returns {!boolean} Model valid state
   */
  verify () {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return false
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    if (fbeFullSize < this.model.fbeSize) {
      return false
    }

    return this.model.verify()
  }

  /**
   * Create a new model (begin phase)
   * @this {!ConfigHistoryRequestModel}
   * @returns {!number} Model begin offset
   */
  createBegin () {
    return this.buffer.allocate(4 + this.model.fbeSize)
  }

  /**
   * Create a new model (end phase)
   * @this {!ConfigHistoryRequestModel}
   * @param {!number} fbeBegin Model begin offset
   */
  createEnd (fbeBegin) {
    let fbeEnd = this.buffer.size
    let fbeFullSize = fbeEnd - fbeBegin
    this.writeUInt32(this.model.fbeOffset - 4, fbeFullSize)
    return fbeFullSize
  }

  /**
   * Serialize the struct value
   * @this {!ConfigHistoryRequestModel}
   * @param {!ConfigHistoryRequest} value ConfigHistoryRequest value
   * @return {!number} Model begin offset
   */
  serialize (value) {
    let fbeBegin = this.createBegin()
    this.model.set(value)
    return this.createEnd(fbeBegin)
  }

  /**
   * Deserialize the struct value
   * @this {!ConfigHistoryRequestModel}
   * @param {!ConfigHistoryRequest} value ConfigHistoryRequest value, defaults is new ConfigHistoryRequest()
   * @return {!object} Deserialized ConfigHistoryRequest value and its size
   */
  deserialize (value = new ConfigHistoryRequest()) {
    if ((this.buffer.offset + this.model.fbeOffset - 4) > this.buffer.size) {
      return { value: new ConfigHistoryRequest(), size: 0 }
    }

    let fbeFullSize = this.readUInt32(this.model.fbeOffset - 4)
    console.assert((fbeFullSize >= this.model.fbeSize), 'Model is broken!')
    if (fbeFullSize < this.model.fbeSize) {
      return { value: new ConfigHistoryRequest(), size: 0 }
    }

    this.model.get(value)
    return { value: value, size: fbeFullSize }
  }

  /**
   * Move to the next struct value
   * @this {!ConfigHistoryRequestModel}
   * @param {!number} prev Previous ConfigHistoryRequest model size
   */
  next (prev) {
    this.model.fbeShift(prev)
  }
}

export { ConfigHistoryRequestModel };
ConfigHistoryRequest.__has_response = true;
ConfigHistoryRequest.__response_class = ConfigHistoryResponse;
